import { useEffect, useRef, useState } from 'react';
import { ContentProcessingLogSchema } from '../../../../../definitions/schemas';
import {
  ContentProcessingStatus,
  ContentProcessors,
} from '../../../../../enums/ContentProcessingLogs';
import ContentProcessingLogsInterface from '../../../../../graphql/Status/ContentProcessingLogsInterface';
import OrganizationsStore from '../../../../../stores/observables/OrganizationsStore';
import Poll from '../../../../../Services/Poll';

const getDefaultProcessorFromOrg = (): ContentProcessors => {
  const currentOrg = OrganizationsStore.getCurrentOrganization();
  return currentOrg?.convertApiEnabled
    ? ContentProcessors.ConvertapiDocumentToPdf
    : ContentProcessors.LambdaDocumentToPdf;
};

export interface UseConversionStatusPollingProps {
  contentId: string;
  contentProcessingLog?: ContentProcessingLogSchema;
  userContactId?: string;
}

export const useConversionStatusPolling = (
  props: UseConversionStatusPollingProps
) => {
  const { contentId, contentProcessingLog, userContactId } = props;

  const [conversionStatus, setConversionStatus] = useState(
    contentProcessingLog?.status || ContentProcessingStatus.Completed
  );
  const [errorConversion, setErrorConversion] = useState(false);
  const [conversionStatusLoading, setConversionStatusLoading] = useState(
    conversionStatus === ContentProcessingStatus.Processing
  );

  const { current: poll } = useRef(new Poll(() => loadConversionStatus()));
  const { current: processor } = useRef(
    contentProcessingLog?.processor || getDefaultProcessorFromOrg()
  );

  useEffect(() => {
    if (conversionStatus === ContentProcessingStatus.Processing) {
      poll.resume();
    }

    return () => poll.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConversionStatus = async () => {
    try {
      const contentProcessingResponse =
        await new ContentProcessingLogsInterface(
          userContactId
        ).getContentProcessingLogs({
          id: contentId,
          processor,
        });

      const result = contentProcessingResponse.status;
      setConversionStatus(result);
      return result;
    } catch (err) {
      setConversionStatusLoading(false);
      setErrorConversion(true);
      return ContentProcessingStatus.Failed;
    }
  };

  const loadConversionStatus = async () => {
    setConversionStatusLoading(true);

    const status = await fetchConversionStatus();
    if (status === ContentProcessingStatus.Processing) {
      return;
    }

    setConversionStatusLoading(false);
    setErrorConversion(status === ContentProcessingStatus.Failed);
    poll.stop();
  };

  return { conversionStatusLoading, conversionStatus, errorConversion };
};
